import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import useQuestionResponse from "../useQuestionResponse";
import ListComments from "./ListComments";
import { useMemo } from "react";
import { CommentsTurnedOff, NoComments } from "./CommentStatuses";
import {
    Card,
    CardContent,
    CardHeader,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "frontend-components";

function CommentHeader({ children }: { children: ReactNode }) {
    const { t } = useTranslation();
    const { question, questionIndex, answersWithComments } = useQuestionResponse();
    if (answersWithComments.length === 0) return null;
    return (
        <CardHeader className="flex justify-end underline text-sm">
            <Dialog>
                <DialogTrigger>{t("responseOverview.allCommentsTrigger")}</DialogTrigger>
                <DialogContent className="flex flex-col">
                    <DialogHeader>
                        <DialogTitle>{t("responseOverview.allCommentsTitle")}</DialogTitle>
                    </DialogHeader>
                    <div className="p-4 overflow-y-scroll grow max-h-[calc(100vh-8rem)]">
                        <h2 className="text-lg pb-4 whitespace-pre-wrap">
                            {questionIndex}. {question.questionText}
                        </h2>
                        <ListComments>{children}</ListComments>
                    </div>
                </DialogContent>
            </Dialog>
        </CardHeader>
    );
}

function Comments({ children }: { children: ReactNode }) {
    const { question, answersWithComments } = useQuestionResponse();

    const variation = useMemo(() => {
        if (!question.allowComments) return <CommentsTurnedOff />;
        if (answersWithComments.length === 0) return <NoComments />;
        return <ListComments limit={4}>{children}</ListComments>;
    }, [question, answersWithComments, children]);

    return <CardContent>{variation}</CardContent>;
}

function CommentLayout({ children }: { children?: ReactNode }) {
    return <Card className="w-full lg:min-w-2/5 lg:w-2/5 shadow-sm">{children}</Card>;
}

export { CommentLayout, Comments, CommentHeader };
